/**
 * Utility functions for transforming data between Grading Scales page format and API format
 * Based on API_DOCUMENTATION_FOR_REDUX.md
 */

export interface GradeRangeForm {
  id: string;
  grade: string;
  min: string;
  max: string;
  description: string;
}

export interface GradeRangeAPI {
  grade: string;
  minValue: string;
  maxValue: string;
  description?: string;
  sortOrder?: number;
}

export interface GradingScaleForm {
  scaleName: string;
  scaleType: 'percentage' | 'points' | 'custom';
  gradeRanges: GradeRangeForm[];
}

export interface GradingScaleAPI {
  name: string;
  type: "Percentage" | "Points" | "Custom";
  ranges: GradeRangeAPI[];
}

/**
 * Transform form data to API format
 * According to API_DOCUMENTATION_FOR_REDUX.md
 * 
 * API expects minValue/maxValue as strings directly
 * No transformation needed - just map field names
 */
export const transformToAPI = (formData: GradingScaleForm): GradingScaleAPI => {
  // Convert scaleType to API type format
  const typeMapping: { [key: string]: "Percentage" | "Points" | "Custom" } = {
    'percentage': 'Percentage',
    'points': 'Points',
    'custom': 'Custom'
  };
  
  return {
    name: formData.scaleName,
    type: typeMapping[formData.scaleType] || 'Percentage',
    ranges: formData.gradeRanges.map((range, index) => ({
      grade: range.grade,
      minValue: range.min,
      maxValue: range.max,
      description: range.description || '',
      sortOrder: index
    }))
  };
};

/**
 * Transform API data to form format
 * 
 * Reverse transformation from API to page form
 */
export const transformFromAPI = (
  apiData: { name: string; type: string; ranges: GradeRangeAPI[] }
): GradingScaleForm => {
  // Convert API type to form scaleType
  const typeMapping: { [key: string]: 'percentage' | 'points' | 'custom' } = {
    'Percentage': 'percentage',
    'Points': 'points',
    'Custom': 'custom'
  };
  
  return {
    scaleName: apiData.name,
    scaleType: typeMapping[apiData.type] || 'percentage',
    gradeRanges: apiData.ranges
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
      .map((item, index) => ({
        id: Date.now().toString() + index,
        grade: item.grade,
        min: item.minValue,
        max: item.maxValue,
        description: item.description || ''
      }))
  };
};

/**
 * Validate grade ranges don't overlap (for percentage and points types)
 */
export const validateNoOverlap = (ranges: GradeRangeForm[], scaleType: string): boolean => {
  if (scaleType === 'custom') return true;
  
  const numericRanges = ranges
    .filter(r => r.min && r.max)
    .map(r => ({
      min: parseFloat(r.min),
      max: parseFloat(r.max)
    }))
    .sort((a, b) => a.min - b.min);
  
  for (let i = 0; i < numericRanges.length - 1; i++) {
    if (numericRanges[i].max >= numericRanges[i + 1].min) {
      return false; // Overlap detected
    }
  }
  
  return true;
};

/**
 * Example transformations for testing:
 * 
 * Form data (Percentage type):
 * {
 *   scaleName: "Standard Scale",
 *   scaleType: "percentage",
 *   gradeRanges: [
 *     { id: "1", grade: "A", min: "90", max: "100", description: "Excellent" },
 *     { id: "2", grade: "B", min: "80", max: "89", description: "Good" }
 *   ]
 * }
 * 
 * Transforms to API:
 * {
 *   name: "Standard Scale",
 *   type: "Percentage",
 *   ranges: [
 *     { grade: "A", minValue: "90", maxValue: "100", description: "Excellent", sortOrder: 0 },
 *     { grade: "B", minValue: "80", maxValue: "89", description: "Good", sortOrder: 1 }
 *   ]
 * }
 */

